import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });

    // Fast-toggle on touch: native <details> toggle only fires on `click`,
    // which lands ~60 ms after `touchend` on iOS. For menu-drawer
    // accordions, toggle on `touchend` so the interaction feels immediate.
    // `touchend` fires only when the user actually lifts on the summary
    // (scroll cancels the touch on this target), so it's safe for both
    // open and close. PreventDefault on the subsequent click stops the
    // native activation from flipping us back.
    if (this.closest('.menu-drawer')) {
      let toggledOnTouch = false;
      this.summary.addEventListener(
        'touchend',
        () => {
          if (
            (isMobileBreakpoint() && this.#disableOnMobile) ||
            (!isMobileBreakpoint() && this.#disableOnDesktop)
          ) {
            return;
          }
          this.details.open = !this.details.open;
          toggledOnTouch = true;
        },
        { signal, passive: true }
      );
      this.summary.addEventListener(
        'click',
        (event) => {
          if (toggledOnTouch) {
            // Prevent the native click-activation from flipping us back.
            event.preventDefault();
            toggledOnTouch = false;
          }
        },
        { signal }
      );
    }
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Block the toggle entirely when disabled at the current breakpoint
    // (e.g. menu blocks render expanded on desktop and shouldn't collapse).
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // Otherwise let the browser's native <details> activation toggle `open`.
    // We previously preventDefault()'d + toggled manually to work around an
    // iOS "double-tap inside overflow container" bug, but that explicit
    // toggle races with iOS Safari's own touchend-based native toggle and
    // ends up cancelling it — taps would fire the touch highlight but the
    // accordion wouldn't change state until a second/third tap. The
    // menu-drawer touchend handler above covers the snappy-feel case; for
    // every other accordion the native click path is reliable on its own.
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
